import {
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import {
  cacheLoadingErrorTooltip,
  cacheLoadingGeneralTooltip,
  positionsNotApplicableMessage,
  ResizeHandleState,
} from '@helios/generic-components'
import {
  EventType,
  HeliosMessageType,
  SessionBasedEvent,
  SessionMessagingService,
} from '@helios/messaging'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  clearFocusedExtPosId,
  RootState,
  selectActiveSession,
  selectFocusedPositionSecurity,
  selectIsEnfusionSession,
  selectPositionsSessionCacheStatus,
  SessionCacheStatus,
  setFocusedExtPosId,
  setFocusedSecurities,
  setFocusedSubSleeveAccountId,
  setTransactionCurrencyCode,
  setTransactionSecurityId,
  storeSessionPositionDataAvailableTime,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ComponentContainer } from 'golden-layout'
import { Subject } from 'rxjs'
import {
  distinctUntilChanged,
  map,
  take,
  takeUntil,
  throttleTime,
} from 'rxjs/operators'
import { ExternalComparisonPanelComponent } from './external-comparison/external-comparison-panel.component'
import { ExternalPositionDetailPanelComponent } from './external-position-detail/external-position-detail-panel.component'
import { HistoricalBalanceComparisonPanelComponent } from './historical-balance-comparison/historical-balance-comparison-panel.component'
import { Position } from './model/positions.model'
import { PositionsGridComponent } from './positions-grid/positions-grid.component'

interface PositionsPanelState {
  resizeHandleState: ResizeHandleState
}

@Component({
  selector: 'hs-positions',
  templateUrl: './positions-panel.component.html',
  styleUrls: ['./positions-panel.component.scss'],
  exportAs: 'PositionsPanel',
})
export class PositionsPanelComponent
  extends BasePanelComponent<PositionsPanelState>
  implements OnInit, OnDestroy
{
  /**
   * @internal for testing
   */
  @ViewChild('positionsGrid')
  readonly positionsGridComponent!: PositionsGridComponent
  /**
   * @internal for testing
   */
  @ViewChild('externalComparison')
  readonly externalComparisonComponent!: ExternalComparisonPanelComponent
  /**
   * @internal for testing
   */
  @ViewChild('historicalBalanceComparison')
  readonly historicalBalanceComparisonComponent!: HistoricalBalanceComparisonPanelComponent

  /**
   * @internal for testing
   */
  @ViewChild('externalPositionDetail')
  readonly externalPositionDetailComponent!: ExternalPositionDetailPanelComponent

  /**
   * attribute needed for automated testing, so we know when the cache has loaded.
   * value is passed to the cache-load-listener component to control when the grid is displayed
   */
  @HostBinding('attr.data-cy-cache-status')
  sessionCacheStatus = SessionCacheStatus.UNKNOWN

  /**
   * Row/Position selected in positions ag-grid.
   */
  isMultipleSelected = false
  selectedPosition: Position | null = null

  currentSessionId?: string
  currentAccountIds?: number[]

  /**
   * True when the active session is Enfusion-sourced (`kind === 'ENFN'`). The grid is
   * then never created and a static message card is shown instead of waiting on a
   * session cache that will never become ready for these accounts.
   */
  notApplicable = false
  readonly notApplicableMessage = positionsNotApplicableMessage

  private readonly _destroyed$ = new Subject<void>()

  focusedSecurityId$ = this.store.select(selectFocusedPositionSecurity)

  readonly updatedPositions$ = this.messagingService
    .on<unknown, SessionBasedEvent<string[]>>(EventType.MESSAGE, [
      HeliosMessageType.PositionUpdate,
      HeliosMessageType.PositionDelete,
      HeliosMessageType.PositionCreate,
    ])
    .pipe(
      throttleTime(1000), //position/create is very noisy, we can reduce this to 100ms after PVAL-14086
      map(e => e.payload),
    )

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    private readonly messagingService: SessionMessagingService,
  ) {
    super(container)
  }

  ngOnInit() {
    this.store
      .select(selectIsEnfusionSession)
      .pipe(takeUntil(this._destroyed$), distinctUntilChanged())
      .subscribe(isEnfusionSession => {
        this.notApplicable = isEnfusionSession
        if (isEnfusionSession) {
          // nothing is loading and nothing will be re-enabled - drop any cache tooltip
          this.setTitleTooltip(undefined)
        }
      })

    this.store
      .select(selectPositionsSessionCacheStatus)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(status => this.onCacheStatusUpdate(status))

    this.store
      .select(selectActiveSession)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged((a, b) => a?.id === b?.id),
      )
      .subscribe(() => (this.selectedPosition = null))
  }

  ngOnDestroy(): void {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  setSecurityIdFilter(securityId: number) {
    this.store.dispatch(setTransactionSecurityId(securityId))
  }

  setCurrencyFilter(currency: string) {
    this.store.dispatch(setTransactionCurrencyCode(currency))
  }

  /** selectedPosition is used as input to the subpanels */
  setSelectedPosition(values: Position[] | null) {
    this.selectedPosition = values ? values[0] : null
    this.isMultipleSelected = values && values.length > 1 ? true : false
    const selectedPositionAccountId = values?.map(value => value.acctId)
    const securities: number[] = []
    values?.forEach(value => {
      securities.push(value?.secId)
    })
    if (securities.length > 0) {
      this.store.dispatch(setFocusedSecurities({ securityIds: securities }))
      if (selectedPositionAccountId)
        this.store.dispatch(
          setFocusedSubSleeveAccountId({
            subSleeveAccountId: selectedPositionAccountId[0],
          }),
        )
    }
    if (this.selectedPosition?.extPosId) {
      this.store.dispatch(setFocusedExtPosId(this.selectedPosition?.extPosId))
    } else {
      this.store.dispatch(clearFocusedExtPosId())
    }
  }

  onResizeFinished(resizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      resizeHandleState,
    })
  }

  onCacheStatusUpdate(status: SessionCacheStatus): void {
    this.sessionCacheStatus = status
    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.currentSessionId = session?.id
        this.currentAccountIds = session?.loadedAccountIds
      })

    switch (status) {
      case SessionCacheStatus.STARTED:
      case SessionCacheStatus.PARTIAL:
        this.setCacheTitleTooltip(cacheLoadingGeneralTooltip, 'WARNING')
        break
      case SessionCacheStatus.ERROR:
        this.setCacheTitleTooltip(cacheLoadingErrorTooltip, 'ERROR')
        break
      case SessionCacheStatus.UNKNOWN:
      case SessionCacheStatus.DONE:
        if (this.currentSessionId) {
          this.store.dispatch(
            storeSessionPositionDataAvailableTime({
              sessionId: this.currentSessionId,
              positionDataAvailableTime: Date.now(),
            }),
          )
        }
        this.setTitleTooltip(undefined)
        break
      default:
        this.setTitleTooltip(undefined)
        break
    }
  }

  /**
   * Cache-status tooltips describe a wait that is being suppressed when the panel is
   * not applicable for the session, so they are dropped in that case.
   */
  private setCacheTitleTooltip(
    tooltip: string,
    severity: 'ERROR' | 'WARNING',
  ): void {
    if (this.notApplicable) {
      this.setTitleTooltip(undefined)
      return
    }
    this.setTitleTooltip(tooltip, severity)
  }
}
